import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Upload, Loader2, Trash2, Plus, FileText, Check, AlertCircle, ShieldCheck } from "lucide-react";

const CSV_TEMPLATE = `name,area,service,rating,comment,date
Sarah W,LS8 Roundhay,Garden Maintenance,5,"Really pleased, pro turned up on time and left the place spotless",2026-05
James M,LS18 Horsforth,Lawn Care,5,"Great value and friendly team",2026-04`;

// Robust CSV parser that handles quoted fields with commas and escaped quotes.
function parseCSV(text) {
  const rows = [];
  let i = 0, field = "", row = [], inQuotes = false;
  const pushField = () => { row.push(field); field = ""; };
  const pushRow = () => { rows.push(row); row = []; };
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i+1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQuotes = false; i++; continue; }
      field += c; i++;
    } else {
      if (c === '"') { inQuotes = true; i++; continue; }
      if (c === ',') { pushField(); i++; continue; }
      if (c === '\n' || c === '\r') {
        pushField(); if (row.length > 1 || row[0] !== "") pushRow();
        if (c === '\r' && text[i+1] === '\n') i += 2; else i++; continue;
      }
      field += c; i++;
    }
  }
  if (field !== "" || row.length) { pushField(); pushRow(); }
  if (rows.length === 0) return [];
  // Normalise headers
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const findIdx = (names) => {
    for (const n of names) { const idx = headers.indexOf(n); if (idx >= 0) return idx; }
    return -1;
  };
  const colName = findIdx(["name", "customer", "customer name"]);
  const colArea = findIdx(["area", "postcode", "location"]);
  const colService = findIdx(["service", "category"]);
  const colRating = findIdx(["rating", "stars"]);
  const colComment = findIdx(["comment", "review", "feedback", "quote"]);
  const colDate = findIdx(["date", "when", "month"]);
  return rows.slice(1).map((row) => ({
    customer_name: colName >= 0 ? (row[colName] || "").trim() : "",
    area: colArea >= 0 ? (row[colArea] || "").trim() : "",
    service: colService >= 0 ? (row[colService] || "").trim() : "",
    rating: colRating >= 0 ? Math.min(5, Math.max(1, parseInt(row[colRating] || "5", 10) || 5)) : 5,
    comment: colComment >= 0 ? (row[colComment] || "").trim() : "",
    date: colDate >= 0 ? (row[colDate] || "").trim() : "",
    verified: true,
  })).filter((r) => r.customer_name && r.comment);
}

export default function ReviewsManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [importError, setImportError] = useState("");
  const [tab, setTab] = useState("bulk");

  // One-by-one form
  const [form, setForm] = useState({ customer_name: "", area: "", service: "", rating: 5, comment: "", date: "", verified: true });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/testimonials?limit=100&offset=0");
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCsvChange = (text) => {
    setCsv(text);
    setImportError("");
    setImportMsg("");
    if (!text.trim()) { setPreview([]); return; }
    try {
      const parsed = parseCSV(text);
      setPreview(parsed);
      if (parsed.length === 0) setImportError("No valid rows found. Check that the first row is headers: name,area,service,rating,comment,date");
    } catch (e) { setImportError("Could not parse CSV: " + e.message); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => onCsvChange(ev.target.result);
    reader.readAsText(f);
  };

  const commitImport = async () => {
    if (preview.length === 0) return;
    setImporting(true); setImportError(""); setImportMsg("");
    try {
      // Chunk into batches of 200 to stay well under the 500-item API cap
      const CHUNK = 200;
      let inserted = 0;
      for (let i = 0; i < preview.length; i += CHUNK) {
        const batch = preview.slice(i, i + CHUNK);
        const r = await api.post("/admin/testimonials/bulk", { items: batch });
        inserted += r.data.inserted || batch.length;
      }
      setImportMsg(`Imported ${inserted} reviews.`);
      setCsv(""); setPreview([]);
      await load();
    } catch (err) {
      setImportError(formatErr(err?.response?.data?.detail) || err?.message || "Import failed");
    } finally { setImporting(false); }
  };

  const addOne = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.comment.trim()) { setAddError("Name and comment are required."); return; }
    setAdding(true); setAddError("");
    try {
      await api.post("/admin/testimonials", form);
      setForm({ customer_name: "", area: "", service: "", rating: 5, comment: "", date: "", verified: true });
      await load();
    } catch (err) {
      setAddError(formatErr(err?.response?.data?.detail) || "Add failed");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); await load(); }
    catch { alert("Delete failed"); }
  };

  return (
    <div data-testid="reviews-manager" className="space-y-6">
      {/* Tab selector */}
      <div className="flex gap-2">
        <button data-testid="rm-tab-bulk" onClick={()=>setTab("bulk")} className={`px-3 py-2 border font-display uppercase tracking-widest text-[11px] ${tab === "bulk" ? "bg-[#8DE038] text-[#050806] border-[#8DE038]" : "border-[#1F3325] text-[#C8D4CB]"}`}>Bulk upload (CSV)</button>
        <button data-testid="rm-tab-add" onClick={()=>setTab("add")} className={`px-3 py-2 border font-display uppercase tracking-widest text-[11px] ${tab === "add" ? "bg-[#8DE038] text-[#050806] border-[#8DE038]" : "border-[#1F3325] text-[#C8D4CB]"}`}>Add one</button>
      </div>

      {tab === "bulk" && (
        <div className="border border-[#1F3325] bg-[#0F1712] p-6" data-testid="rm-bulk">
          <div className="font-display uppercase tracking-widest text-xs text-[#8DE038] mb-2">/ Paste your spreadsheet</div>
          <p className="text-[#9CAEA3] text-sm mb-4">Columns we recognise (first row is headers): <code className="text-[#8DE038]">name</code>, <code className="text-[#8DE038]">area</code>, <code className="text-[#8DE038]">service</code>, <code className="text-[#8DE038]">rating</code>, <code className="text-[#8DE038]">comment</code>, <code className="text-[#8DE038]">date</code>. Blank columns are fine.</p>

          <div className="flex gap-2 mb-3">
            <label className="btn-secondary text-xs cursor-pointer">
              <Upload size={14}/> Upload .csv
              <input data-testid="rm-file" type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden"/>
            </label>
            <button type="button" onClick={() => onCsvChange(CSV_TEMPLATE)} data-testid="rm-template" className="btn-secondary text-xs"><FileText size={14}/> Insert template</button>
          </div>

          <textarea data-testid="rm-csv" value={csv} onChange={(e) => onCsvChange(e.target.value)} rows={12} placeholder="Paste CSV here… e.g. name,area,service,rating,comment,date" className="gl-input font-mono text-xs resize-y"/>

          {importError && <div className="mt-3 text-red-400 text-xs border border-red-500/40 bg-red-900/20 p-2 flex items-center gap-2"><AlertCircle size={14}/> {importError}</div>}
          {importMsg && <div className="mt-3 text-[#8DE038] text-xs border border-[#8DE038]/40 bg-[#8DE038]/10 p-2 flex items-center gap-2"><Check size={14}/> {importMsg}</div>}

          {preview.length > 0 && (
            <div className="mt-4 border border-[#1F3325] bg-[#050806] p-4" data-testid="rm-preview">
              <div className="font-display uppercase tracking-widest text-[11px] text-[#9CAEA3] mb-2">Preview — {preview.length} row(s) ready to import</div>
              <div className="max-h-64 overflow-y-auto text-xs">
                <table className="w-full text-left">
                  <thead className="text-[#8DE038] uppercase tracking-widest text-[10px]">
                    <tr><th className="pb-1 pr-3">Name</th><th className="pr-3">Area</th><th className="pr-3">Service</th><th className="pr-3">★</th><th>Comment</th></tr>
                  </thead>
                  <tbody className="text-[#C8D4CB]">
                    {preview.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-t border-[#1F3325]"><td className="py-1 pr-3">{r.customer_name}</td><td className="pr-3">{r.area || "—"}</td><td className="pr-3">{r.service || "—"}</td><td className="pr-3">{r.rating}</td><td className="truncate max-w-[320px]">{r.comment}</td></tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 20 && <div className="text-[10px] text-[#6B7C71] mt-2">+ {preview.length - 20} more…</div>}
              </div>
              <button data-testid="rm-commit" onClick={commitImport} disabled={importing} className="btn-primary mt-4 w-full justify-center disabled:opacity-60">
                {importing ? <><Loader2 className="animate-spin" size={16}/> Importing {preview.length} reviews…</> : <><Plus size={16}/> Import {preview.length} reviews</>}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "add" && (
        <form onSubmit={addOne} className="border border-[#1F3325] bg-[#0F1712] p-6 space-y-3" data-testid="rm-add">
          <div className="font-display uppercase tracking-widest text-xs text-[#8DE038] mb-2">/ Add one review</div>
          <div className="grid md:grid-cols-2 gap-3">
            <input required placeholder="Customer name (e.g. Sarah W)" value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} className="gl-input" data-testid="rm-add-name"/>
            <input placeholder="Area (e.g. LS8 Roundhay)" value={form.area} onChange={(e)=>setForm({...form, area: e.target.value})} className="gl-input" data-testid="rm-add-area"/>
            <input placeholder="Service (e.g. Garden Maintenance)" value={form.service} onChange={(e)=>setForm({...form, service: e.target.value})} className="gl-input" data-testid="rm-add-service"/>
            <select value={form.rating} onChange={(e)=>setForm({...form, rating: parseInt(e.target.value, 10)})} className="gl-input" data-testid="rm-add-rating">
              {[5,4,3,2,1].map((n)=>(<option key={n} value={n}>{n} ★</option>))}
            </select>
          </div>
          <textarea required placeholder="Review text / quote" rows={3} value={form.comment} onChange={(e)=>setForm({...form, comment: e.target.value})} className="gl-input resize-none" data-testid="rm-add-comment"/>
          <input placeholder="Date (optional, e.g. 2026-05)" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} className="gl-input" data-testid="rm-add-date"/>
          <label className="flex items-center gap-2 text-sm text-[#C8D4CB]"><input type="checkbox" checked={form.verified} onChange={(e)=>setForm({...form, verified: e.target.checked})}/> Mark as verified</label>
          {addError && <div className="text-red-400 text-xs border border-red-500/40 bg-red-900/20 p-2">{addError}</div>}
          <button disabled={adding} type="submit" className="btn-primary w-full justify-center disabled:opacity-60" data-testid="rm-add-submit">{adding ? <Loader2 className="animate-spin" size={16}/> : <><Plus size={16}/> Add review</>}</button>
        </form>
      )}

      {/* Existing reviews list */}
      <div>
        <div className="font-display uppercase tracking-widest text-xs text-[#9CAEA3] mb-3">/ Current reviews ({total})</div>
        {loading ? (
          <div className="text-[#9CAEA3]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">No reviews yet. Add one above.</div>
        ) : (
          <div className="space-y-2">
            {items.map((r) => (
              <div key={r.id} data-testid={`rm-item-${r.id}`} className="border border-[#1F3325] bg-[#0F1712] p-3 grid md:grid-cols-12 gap-3 text-sm">
                <div className="md:col-span-2 font-display uppercase tracking-widest text-xs text-[#F0F4F1] truncate flex items-center gap-1">{r.verified && <ShieldCheck size={11} className="text-[#8DE038]"/>}{r.customer_name}</div>
                <div className="md:col-span-2 text-[#9CAEA3] text-xs truncate">{r.area || "—"}</div>
                <div className="md:col-span-2 text-[#9CAEA3] text-xs truncate">{r.service || "—"}</div>
                <div className="md:col-span-1 text-[#8DE038] text-xs">{"★".repeat(r.rating)}</div>
                <div className="md:col-span-4 text-[#C8D4CB] text-xs truncate">{r.comment}</div>
                <div className="md:col-span-1 text-right"><button onClick={()=>remove(r.id)} className="text-red-400 hover:text-red-300" aria-label="Delete"><Trash2 size={14}/></button></div>
              </div>
            ))}
            {total > items.length && <div className="text-[10px] text-[#6B7C71] text-center pt-2">Showing {items.length} of {total}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
